import Autocomplete from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type {
  GridRenderCellParams,
  GridRenderEditCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid";
import type { Key, ReactNode, SyntheticEvent } from "react";

import type { SelectOption } from "./SelectCell";

export interface MultiSelectOption extends SelectOption {
  fontColor?: string;
  backgroundColor?: string;
}

export function createMultiSelectViewCellRenderer<
  Row extends GridValidRowModel,
  Option extends SelectOption,
>(optionMap: Map<string, Option>, renderOption: (option: Option) => ReactNode) {
  function MultiSelectViewCellRenderer(
    params: GridRenderCellParams<Row, string[]>,
  ) {
    const values = params.value;
    if (!values || values.length === 0) {
      return null;
    }

    return (
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
        {values.map((value) => {
          const option = optionMap.get(value);
          if (!option) {
            return null;
          }
          return <span key={value}>{renderOption(option)}</span>;
        })}
      </Stack>
    );
  }

  return MultiSelectViewCellRenderer;
}

export function createMultiSelectEditCellRenderer<
  Row extends GridValidRowModel,
  Option extends SelectOption,
>(
  options: Option[],
  renderOption: (option: Option) => ReactNode,
  renderTag?: (
    option: Option,
    tagProps: { key: Key; onDelete: (event: unknown) => void },
  ) => ReactNode,
) {
  function MultiSelectEditCellRenderer(
    params: GridRenderEditCellParams<Row, string[]>,
  ) {
    const currentValues: string[] = params.value ?? [];
    const selectedOptions = options.filter((o) =>
      currentValues.includes(o.value),
    );

    const handleChange = (_event: SyntheticEvent, newValue: Option[]) => {
      const newValues = newValue.map((o) => o.value);
      params.api.setEditCellValue({
        id: params.id,
        field: params.field,
        value: newValues,
      });
    };

    return (
      <Autocomplete<Option, true, false, false>
        multiple
        fullWidth
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(opt, val) => opt.value === val.value}
        renderOption={(props, option, { selected }) => (
          <li
            {...props}
            key={option.value}
            data-testid={`multiselect-option-${option.value}`}
          >
            <Checkbox checked={selected} sx={{ mr: 1 }} />
            {renderOption(option)}
          </li>
        )}
        renderValue={(tagValues, getItemProps) =>
          tagValues.map((option, index) => {
            const { key, ...chipProps } = getItemProps({ index });
            if (renderTag) {
              return renderTag(option, { key, onDelete: chipProps.onDelete });
            }
            return (
              <Chip
                key={key}
                label={option.label}
                size="small"
                {...chipProps}
              />
            );
          })
        }
        renderInput={(inputParams) => (
          <TextField {...inputParams} variant="outlined" size="small" />
        )}
      />
    );
  }

  return MultiSelectEditCellRenderer;
}

function renderChip(option: MultiSelectOption) {
  return (
    <Chip
      label={option.label}
      size="small"
      sx={{
        bgcolor: option.backgroundColor,
        color: option.fontColor,
      }}
    />
  );
}

export function createChipMultiSelectViewCellRenderer<
  Row extends GridValidRowModel,
>(optionMap: Map<string, MultiSelectOption>) {
  return createMultiSelectViewCellRenderer<Row, MultiSelectOption>(
    optionMap,
    renderChip,
  );
}

export function createChipMultiSelectEditCellRenderer<
  Row extends GridValidRowModel,
>(options: MultiSelectOption[]) {
  return createMultiSelectEditCellRenderer<Row, MultiSelectOption>(
    options,
    renderChip,
    (option, { key, onDelete }) => (
      <Chip
        key={key}
        label={option.label}
        size="small"
        onDelete={onDelete}
        sx={{
          bgcolor: option.backgroundColor,
          color: option.fontColor,
        }}
      />
    ),
  );
}
